import { useCallback, useEffect, useMemo, useState } from 'react'

import { Button, Dialog, Icon, Layout, Page, Text, TextField } from '@loadsmart/miranda-react'

import { AnalyticsEvent } from 'core/analytics/events'
import { useAnalytics } from 'core/analytics/useAnalytics'
import { RequestType } from 'core/domain/RequestType'
import AppLayout from 'core/ui/components/AppLayout'
import Header from 'core/ui/components/Header'
import ZendeskDialog from 'core/ui/components/zendesk/TryNowZendeskDialog'
import {
  buildDemoSchedule,
  demoScheduleReport,
  DEMO_SCHEDULES,
  DEMO_SCHEDULE_DRAFT,
  DEMO_SCHEDULE_FREQUENCIES,
  DEMO_SCHEDULE_HIGHLIGHTS,
  DEMO_SCHEDULE_MONTH_DAYS,
  DEMO_SCHEDULE_REPORTS,
  DEMO_SCHEDULE_TIMES,
  DEMO_SCHEDULE_TIMEZONES,
  DEMO_SCHEDULE_WEEKDAYS,
  type DemoSchedule,
  type DemoScheduleDraft,
  type DemoScheduleFrequency,
} from 'managed-analytics/domain/demoScheduler'
import {
  StyledActions,
  StyledCell,
  StyledCellMuted,
  StyledClosingAction,
  StyledClosingBand,
  StyledClosingBody,
  StyledClosingCopy,
  StyledClosingTitle,
  StyledContentColumn,
  StyledDisclaimer,
  StyledEyebrow,
  StyledHeadline,
  StyledHeadlineSoft,
  StyledHeaderCell,
  StyledHighlight,
  StyledHighlightGrid,
  StyledLede,
  StyledPanel,
  StyledPanelBadge,
  StyledPanelHeader,
  StyledPanelTitle,
  StyledRow,
  StyledSection,
  StyledSegment,
  StyledSegmented,
  StyledStatusPill,
  StyledTable,
  StyledTableScroller,
} from 'managed-analytics/ui/components/demoFeature/DemoFeature.styles'
import {
  StyledCreatedMarker,
  StyledCreatedNote,
  StyledFormField,
  StyledFormGrid,
  StyledFormLabel,
  StyledInlineAction,
  StyledSelect,
} from 'managed-analytics/ui/DemoSchedulePage.styles'

/**
 * The Scheduler preview, at /demo/schedule. Unlike the other previews this one
 * is not read-only: the visitor builds a schedule and watches the row appear.
 */

const TRACK_EVENT_EXPIRY_MS = 1 * 60 * 1000

const LAST_TRACKED_STORAGE_KEY = 'lastTrackedDemoSchedulerEvent'

const LEAD_TITLE = 'Talk to our team'

type LeadIntent = {
  source: string
  description: string
}

const HERO_LEAD: LeadIntent = {
  source: 'Hero',
  description:
    'Tell us where to reach you and we will set up scheduled delivery on the reports your team actually reads.',
}

const CREATED_LEAD: LeadIntent = {
  source: 'Sample schedule',
  description:
    'You built a schedule — tell us where to reach you and we will turn it on against your own reports.',
}

const CLOSING_LEAD: LeadIntent = {
  source: 'Closing band',
  description:
    'Tell us which report should land in whose inbox, and how often. We will get back to you about setting it up.',
}

export default function DemoSchedulePage() {
  const { track } = useAnalytics()

  const [schedules, setSchedules] = useState<Array<DemoSchedule>>(DEMO_SCHEDULES)
  const [createdScheduleId, setCreatedScheduleId] = useState<string | null>(null)
  const [isFormOpen, setIsFormOpen] = useState(false)
  const [draft, setDraft] = useState<DemoScheduleDraft>(DEMO_SCHEDULE_DRAFT)
  const [leadIntent, setLeadIntent] = useState<LeadIntent | null>(null)
  const [isLeadDialogOpen, setIsLeadDialogOpen] = useState(false)

  const selectedReport = demoScheduleReport(draft.reportId)
  const profiles = useMemo(
    () => [...new Set(DEMO_SCHEDULE_REPORTS.map(report => report.profile))],
    []
  )
  const reportsForProfile = DEMO_SCHEDULE_REPORTS.filter(
    report => report.profile === selectedReport.profile
  )
  const dayOptions =
    draft.frequency === 'Monthly' ? DEMO_SCHEDULE_MONTH_DAYS : DEMO_SCHEDULE_WEEKDAYS

  useEffect(() => {
    function shouldTrackEvent() {
      const lastTracked = localStorage.getItem(LAST_TRACKED_STORAGE_KEY)
      if (!lastTracked) return true

      return Date.now() - parseInt(lastTracked, 10) > TRACK_EVENT_EXPIRY_MS
    }

    if (shouldTrackEvent()) {
      track(AnalyticsEvent.OpenDemoScheduler)
      localStorage.setItem(LAST_TRACKED_STORAGE_KEY, Date.now().toString())
    }
  }, [track])

  const openLeadForm = useCallback(
    (intent: LeadIntent) => {
      track(AnalyticsEvent.OpenDemoSchedulerLeadForm, { Source: intent.source })
      setLeadIntent(intent)
      setIsLeadDialogOpen(true)
    },
    [track]
  )

  function openForm() {
    track(AnalyticsEvent.DemoSchedulerOpenForm, { 'Existing Schedules Count': schedules.length })
    setDraft(DEMO_SCHEDULE_DRAFT)
    setIsFormOpen(true)
  }

  // A day means something different per cadence, so it resets with the list —
  // otherwise "Mon" survives onto a monthly send.
  function changeFrequency(frequency: DemoScheduleFrequency) {
    setDraft(current => ({
      ...current,
      frequency,
      day: frequency === 'Monthly' ? DEMO_SCHEDULE_MONTH_DAYS[0] : DEMO_SCHEDULE_WEEKDAYS[0],
    }))
  }

  function changeProfile(profile: string) {
    const firstReport = DEMO_SCHEDULE_REPORTS.find(report => report.profile === profile)
    if (firstReport) setDraft(current => ({ ...current, reportId: firstReport.id }))
  }

  function createSchedule() {
    const schedule = buildDemoSchedule(draft, `sample-${Date.now()}`)

    track(AnalyticsEvent.DemoSchedulerCreateSchedule, {
      'Report Name': schedule.reportName,
      Product: schedule.profile,
      Frequency: draft.frequency,
      Timezone: draft.timezone,
    })

    setSchedules(current => [schedule, ...current])
    setCreatedScheduleId(schedule.id)
    setIsFormOpen(false)
  }

  return (
    <AppLayout>
      <Header
        unauthCtaLabel="Sign in"
        unauthCtaHref="/"
        title={
          <Text color="color-text-primary" variant="body-md-bold">
            Scheduler
          </Text>
        }
      />
      <Page.Content>
        <Page.Body>
          <StyledContentColumn gap="spacing-12">
            <StyledSection gap="spacing-5">
              <StyledEyebrow>
                <Icon name="calendar" size="14px" />
                Included with your Loadsmart reporting
              </StyledEyebrow>
              <StyledHeadline>
                Nobody logs in to check a dashboard every Monday.{' '}
                <StyledHeadlineSoft>Send the report to them instead.</StyledHeadlineSoft>
              </StyledHeadline>
              <StyledLede>
                Pick a report from your library, a cadence and a time. It arrives as an attachment
                in the inbox your team already reads — the carrier scorecard before the Monday
                call, the dock exceptions before the shift starts, the accessorial review on the
                first of the month.
              </StyledLede>
              <StyledActions>
                <Button variant="primary" leading={<Icon name="plus" />} onClick={openForm}>
                  Build a sample schedule
                </Button>
                <Button
                  variant="secondary"
                  leading={<Icon name="send" />}
                  onClick={() => openLeadForm(HERO_LEAD)}
                >
                  {LEAD_TITLE}
                </Button>
              </StyledActions>
            </StyledSection>

            <StyledSection gap="spacing-3">
              <StyledPanel>
                <StyledPanelHeader>
                  <StyledPanelTitle>Scheduled emails</StyledPanelTitle>
                  <StyledPanelBadge>Sample data</StyledPanelBadge>
                  <Button size="small" variant="primary" onClick={openForm}>
                    + Schedule email
                  </Button>
                </StyledPanelHeader>

                <StyledTableScroller>
                  <StyledTable>
                    <thead>
                      <tr>
                        <StyledHeaderCell>Email subject</StyledHeaderCell>
                        <StyledHeaderCell>Profile</StyledHeaderCell>
                        <StyledHeaderCell>Report</StyledHeaderCell>
                        <StyledHeaderCell>Recurrency</StyledHeaderCell>
                        <StyledHeaderCell $align="center">Status</StyledHeaderCell>
                      </tr>
                    </thead>
                    <tbody>
                      {schedules.map(schedule => (
                        <StyledRow key={schedule.id}>
                          <StyledCell>{schedule.subject}</StyledCell>
                          <StyledCell>
                            <StyledCellMuted>{schedule.profile}</StyledCellMuted>
                          </StyledCell>
                          <StyledCell>
                            <StyledCellMuted>{schedule.reportName}</StyledCellMuted>
                          </StyledCell>
                          <StyledCell>
                            <StyledCellMuted>{schedule.recurrence}</StyledCellMuted>
                          </StyledCell>
                          <StyledCell $align="center">
                            <StyledStatusPill
                              $tone={schedule.status === 'Active' ? 'success' : 'neutral'}
                            >
                              {schedule.status}
                            </StyledStatusPill>
                          </StyledCell>
                        </StyledRow>
                      ))}
                    </tbody>
                  </StyledTable>
                </StyledTableScroller>

                {createdScheduleId && (
                  <StyledCreatedNote>
                    <StyledCreatedMarker>
                      <Icon name="check" size="16px" />
                    </StyledCreatedMarker>
                    <span>
                      Your schedule is at the top of the list. On a real account it would send a
                      one-time preview to your inbox right now, so you can see the email before you
                      commit to it — recurring delivery is switched on by our team.{' '}
                      <StyledInlineAction type="button" onClick={() => openLeadForm(CREATED_LEAD)}>
                        <Icon name="send" size="14px" />
                        Get this on my reports
                      </StyledInlineAction>
                    </span>
                  </StyledCreatedNote>
                )}
              </StyledPanel>

              <StyledDisclaimer>
                <Icon name="info" size="14px" />
                Illustrative data. Nothing you schedule here sends an email.
              </StyledDisclaimer>
            </StyledSection>

            <StyledSection gap="spacing-5">
              <Layout.Stack gap="spacing-1">
                <Text variant="heading-lg-bold" color="color-text-primary">
                  Why teams schedule instead of logging in
                </Text>
                <Text variant="body-md" color="color-text-tertiary">
                  The report nobody opens is the report nobody acts on.
                </Text>
              </Layout.Stack>

              <StyledHighlightGrid>
                {DEMO_SCHEDULE_HIGHLIGHTS.map(highlight => (
                  <StyledHighlight key={highlight.title}>
                    <Text variant="body-md-bold" color="color-text-primary">
                      {highlight.title}
                    </Text>
                    <Text variant="body-sm" color="color-text-secondary">
                      {highlight.body}
                    </Text>
                  </StyledHighlight>
                ))}
              </StyledHighlightGrid>
            </StyledSection>

            <StyledClosingBand>
              <StyledClosingCopy>
                <StyledClosingTitle>Which report should land in which inbox?</StyledClosingTitle>
                <StyledClosingBody>
                  Tell us the report, the people and the cadence. We will come back with scheduled
                  delivery set up on your own account.
                </StyledClosingBody>
              </StyledClosingCopy>
              <StyledClosingAction type="button" onClick={() => openLeadForm(CLOSING_LEAD)}>
                <Icon name="send" size="14px" />
                {LEAD_TITLE}
              </StyledClosingAction>
            </StyledClosingBand>
          </StyledContentColumn>
        </Page.Body>
      </Page.Content>

      <Dialog open={isFormOpen} size="large" onClose={() => setIsFormOpen(false)}>
        <Dialog.Header>
          Schedule email
          <Dialog.Close />
        </Dialog.Header>
        <Dialog.Body>
          <StyledFormGrid>
            <StyledFormField $span={2}>
              <StyledFormLabel htmlFor="demo-schedule-subject">Email subject</StyledFormLabel>
              <TextField
                id="demo-schedule-subject"
                value={draft.subject}
                placeholder="Monday freight digest"
                onChange={event =>
                  setDraft(current => ({ ...current, subject: event.target.value }))
                }
              />
            </StyledFormField>

            <StyledFormField>
              <StyledFormLabel htmlFor="demo-schedule-profile">Profile</StyledFormLabel>
              <StyledSelect
                id="demo-schedule-profile"
                value={selectedReport.profile}
                onChange={event => changeProfile(event.target.value)}
              >
                {profiles.map(profile => (
                  <option key={profile} value={profile}>
                    {profile}
                  </option>
                ))}
              </StyledSelect>
            </StyledFormField>

            <StyledFormField>
              <StyledFormLabel htmlFor="demo-schedule-report">Report</StyledFormLabel>
              <StyledSelect
                id="demo-schedule-report"
                value={draft.reportId}
                onChange={event =>
                  setDraft(current => ({ ...current, reportId: event.target.value }))
                }
              >
                {reportsForProfile.map(report => (
                  <option key={report.id} value={report.id}>
                    {report.reportName}
                  </option>
                ))}
              </StyledSelect>
            </StyledFormField>

            <StyledFormField $span={2}>
              <StyledFormLabel>Frequency</StyledFormLabel>
              <StyledSegmented role="group" aria-label="Frequency">
                {DEMO_SCHEDULE_FREQUENCIES.map(frequency => (
                  <StyledSegment
                    key={frequency}
                    type="button"
                    $active={frequency === draft.frequency}
                    aria-pressed={frequency === draft.frequency}
                    onClick={() => changeFrequency(frequency)}
                  >
                    {frequency}
                  </StyledSegment>
                ))}
              </StyledSegmented>
            </StyledFormField>

            {draft.frequency !== 'Daily' && (
              <StyledFormField>
                <StyledFormLabel htmlFor="demo-schedule-day">
                  {draft.frequency === 'Monthly' ? 'Day of month' : 'Day of week'}
                </StyledFormLabel>
                <StyledSelect
                  id="demo-schedule-day"
                  value={draft.day}
                  onChange={event => setDraft(current => ({ ...current, day: event.target.value }))}
                >
                  {dayOptions.map(day => (
                    <option key={day} value={day}>
                      {day}
                    </option>
                  ))}
                </StyledSelect>
              </StyledFormField>
            )}

            <StyledFormField>
              <StyledFormLabel htmlFor="demo-schedule-time">Time</StyledFormLabel>
              <StyledSelect
                id="demo-schedule-time"
                value={draft.time}
                onChange={event => setDraft(current => ({ ...current, time: event.target.value }))}
              >
                {DEMO_SCHEDULE_TIMES.map(time => (
                  <option key={time} value={time}>
                    {time}
                  </option>
                ))}
              </StyledSelect>
            </StyledFormField>

            <StyledFormField>
              <StyledFormLabel htmlFor="demo-schedule-timezone">Timezone</StyledFormLabel>
              <StyledSelect
                id="demo-schedule-timezone"
                value={draft.timezone}
                onChange={event =>
                  setDraft(current => ({ ...current, timezone: event.target.value }))
                }
              >
                {DEMO_SCHEDULE_TIMEZONES.map(timezone => (
                  <option key={timezone} value={timezone}>
                    {timezone}
                  </option>
                ))}
              </StyledSelect>
            </StyledFormField>
          </StyledFormGrid>
        </Dialog.Body>
        <Dialog.Actions>
          <Dialog.ActionSecondary onClick={() => setIsFormOpen(false)}>Cancel</Dialog.ActionSecondary>
          <Dialog.ActionPrimary onClick={createSchedule}>Create schedule</Dialog.ActionPrimary>
        </Dialog.Actions>
      </Dialog>

      {leadIntent && (
        <ZendeskDialog
          requestType={RequestType.TryItNow}
          title={LEAD_TITLE}
          description={leadIntent.description}
          open={isLeadDialogOpen}
          onClose={() => setIsLeadDialogOpen(false)}
        />
      )}
    </AppLayout>
  )
}
